import { useState, useRef } from "react";
import { X, Upload, AlertCircle } from "lucide-react";
import type { ApplicationCondition } from "src/types/conditions";
import { processFileForUpload } from "src/utils/conditionFileValidation";
import {
  getConditionName,
  getConditionStatus,
  getConditionDescription,
  isConditionPending,
} from "src/utils/conditionUtils";

interface ConditionsListProps {
  conditions: ApplicationCondition[];
  onFileSelect: (conditionId: string, file: File | null) => void;
  uploadedFiles: Map<string, File>;
}

/**
 * Determines the badge color based on condition status.
 * Maps different statuses to visual indicators using Tailwind classes.
 *
 * @param status - The condition status (optional)
 * @returns Tailwind CSS classes for styling the badge
 */
const getStatusBadgeColor = (status: string | undefined): string => {
  const statusLower = (status || "").toLowerCase();

  if (statusLower.includes("pending") || statusLower.includes("todo")) {
    return "bg-yellow-100 text-yellow-800";
  }
  if (statusLower.includes("complete") || statusLower.includes("satisfied")) {
    return "bg-green-100 text-green-800";
  }
  if (statusLower.includes("waived")) {
    return "bg-gray-100 text-gray-800";
  }

  return "bg-blue-100 text-blue-800";
};

export default function ConditionsList({
  conditions,
  onFileSelect,
  uploadedFiles,
}: ConditionsListProps) {
  const fileInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const [errors, setErrors] = useState<Map<string, string>>(new Map());
  const [isProcessing, setIsProcessing] = useState<Set<string>>(new Set());

  const handleFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>,
    conditionId: string,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setIsProcessing((prev) => new Set(prev).add(conditionId));
      try {
        const processedFile = await processFileForUpload(file);
        onFileSelect(conditionId, processedFile);
        // Clear any previous errors for this condition
        setErrors((prev) => {
          const newErrors = new Map(prev);
          newErrors.delete(conditionId);
          return newErrors;
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to process file";
        setErrors((prev) => new Map(prev).set(conditionId, errorMessage));
      } finally {
        setIsProcessing((prev) => {
          const newSet = new Set(prev);
          newSet.delete(conditionId);
          return newSet;
        });
      }
    }
    // Reset input
    if (fileInputRefs.current.get(conditionId)) {
      const input = fileInputRefs.current.get(conditionId);
      if (input) input.value = "";
    }
  };

  const handleRemoveFile = (conditionId: string) => {
    onFileSelect(conditionId, null);
  };

  // Filter to show only pending/todo conditions that still need document uploads
  const pendingConditions = conditions.filter(isConditionPending);

  if (pendingConditions.length === 0) {
    return (
      <div className="text-center py-6">
        <p className="text-sm text-gray-600">
          All conditions have been satisfied or waived!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        <p>
          Upload documents to resolve the following{" "}
          <strong>{pendingConditions.length}</strong> condition
          {pendingConditions.length !== 1 ? "s" : ""}
        </p>
      </div>

      {pendingConditions.map((condition) => {
        const hasFile = uploadedFiles.has(condition.id || "");
        const file = uploadedFiles.get(condition.id || "");

        return (
          <div
            key={condition.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="font-medium text-gray-900">
                    {getConditionName(condition) || "Condition"}
                  </h4>
                  <span
                    className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(
                      getConditionStatus(condition),
                    )}`}
                  >
                    {getConditionStatus(condition) || "Pending"}
                  </span>
                </div>
                {getConditionDescription(condition) && (
                  <p className="text-sm text-gray-600">
                    {getConditionDescription(condition)}
                  </p>
                )}
              </div>
            </div>

            {/* File Upload Area */}
            <div className="mt-3">
              {hasFile && file ? (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded p-3">
                  <div className="flex items-center gap-2 text-sm text-blue-900">
                    <Upload size={16} />
                    <span className="truncate font-medium">{file.name}</span>
                  </div>
                  <button
                    onClick={() => handleRemoveFile(condition.id || "")}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    aria-label="Remove file"
                  >
                    <X size={18} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() =>
                    fileInputRefs.current.get(condition.id || "")?.click()
                  }
                  disabled={isProcessing.has(condition.id || "")}
                  className="w-full border-2 border-dashed border-gray-300 rounded p-3 text-center hover:border-blue-500 hover:bg-blue-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <p className="text-sm text-gray-600">
                    {isProcessing.has(condition.id || "")
                      ? "Processing..."
                      : "Click to upload"}{" "}
                    <strong className="text-gray-900">a file</strong>
                  </p>
                </button>
              )}
              {/* Error Message */}
              {errors.has(condition.id || "") && (
                <div className="mt-2 flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>{errors.get(condition.id || "")}</span>
                </div>
              )}
              <input
                ref={(ref) => {
                  if (ref) {
                    fileInputRefs.current.set(condition.id || "", ref);
                  }
                }}
                type="file"
                onChange={(e) => {
                  void handleFileSelect(e, condition.id || "");
                }}
                accept=".pdf,.png,.jpg,.jpeg"
                disabled={isProcessing.has(condition.id || "")}
                className="hidden"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
